import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { View } from 'react-native';

import { DETAIL_EPISODE_STRIPE_ID } from 'config/testID';

import { DetailsType } from 'store/details/actions.types';

import { ContentType } from 'services/data-manager/types';

import { isRecordingItem } from 'utils/recordings';
import { isTabletOrWeb } from 'utils/responsiveness';

import { noSeasonName } from 'screens/Detail/components/Seasons/constants.ts';
import SeasonsTabs from 'screens/Detail/components/SeasonsTabs';
import { RailLoadingStatus } from 'screens/Modular/types';

import Ribbon from 'components/Ribbon';
import useStripeLoadingAnimation from 'components/Stripe/hooks/useStripeLoadingAnimation';
import StripeSkeletonLoading from 'components/StripeSkeletonLoading';

import SeasonBottomSheet from './components/SeasonBottomSheet';
import { SeasonsSelectorWide } from './components/SeasonsSelectorWide';
import useSeasonsAndEpisodes from './hooks/useSeasonsAndEpisodes.ts';
import styles from './styles';
import { SeasonListItem } from './types.ts';

interface IProps {
    templateId: string;
    channelId: string | undefined;
    seriesId: string;
    isVod: boolean;
    initialSeason: typeof noSeasonName | number;
    detailsId: string | undefined;
    onSelectEpisode(episode: DetailsType): void;
}

const Selector = isTabletOrWeb ? SeasonsSelectorWide : SeasonBottomSheet;

export const Seasons: React.FC<IProps> = ({
    templateId,
    channelId,
    seriesId,
    isVod,
    initialSeason,
    detailsId,
    onSelectEpisode,
}: IProps) => {
    const {
        loading,
        seasonEpisodesLoading,

        showTabs,
        selectedTab,
        selectedSeasonNumber,
        selectedSeason,

        seasons,
        episodes,

        onSetSelectedTab,
        onSetSelectedSeasonNumber,
        onLoadNextEpisodes,
    } = useSeasonsAndEpisodes({
        isVod,
        seriesId,
        channelId,
        initialSeason,
    });
    const prevSeasonNumber = useRef(selectedSeasonNumber);

    const { opacitySharedValue } = useStripeLoadingAnimation(
        loading || seasonEpisodesLoading
            ? RailLoadingStatus.LOADING
            : RailLoadingStatus.LOADED,
    );

    const handleSelectSeason = useCallback(
        async (season: SeasonListItem) => {
            onSetSelectedSeasonNumber(season.seasonNumber);
        },
        [onSetSelectedSeasonNumber],
    );

    const initialScrollIndex = useMemo(() => {
        if (!episodes) {
            return 0;
        }

        const scrollIndex = episodes.findIndex((episode) => {
            if (!episode) {
                return false;
            }

            const episodeId = isRecordingItem(episode)
                ? episode.contentId
                : episode.id;

            return detailsId === episodeId;
        });

        return scrollIndex >= 0 ? scrollIndex : 0;
    }, [episodes, detailsId]);

    useEffect(() => {
        if (
            episodes.length &&
            prevSeasonNumber.current !== selectedSeasonNumber
        ) {
            prevSeasonNumber.current = selectedSeasonNumber;
            onSelectEpisode(episodes[0]);
        }
    }, [selectedSeasonNumber, episodes, onSelectEpisode]);

    return (
        <>
            {loading ? (
                <StripeSkeletonLoading
                    railId="details-season"
                    opacitySharedValue={opacitySharedValue}
                    contentType={ContentType.DetailsEpisode}
                />
            ) : (
                <>
                    {showTabs && (
                        <SeasonsTabs
                            selectedTab={selectedTab}
                            onTabPress={onSetSelectedTab}
                        />
                    )}
                    {seasons.length && selectedSeason != null ? (
                        <Selector
                            onSelect={handleSelectSeason}
                            selected={selectedSeason}
                            list={seasons}
                        />
                    ) : (
                        <View style={styles.emptySelector} />
                    )}
                    {seasonEpisodesLoading ? (
                        <StripeSkeletonLoading
                            railId="details-season"
                            opacitySharedValue={opacitySharedValue}
                            contentType={ContentType.DetailsEpisode}
                        />
                    ) : (
                        <Ribbon
                            testID={`${DETAIL_EPISODE_STRIPE_ID}_`}
                            key={`${selectedTab}-${selectedSeasonNumber}`}
                            // @ts-ignore
                            items={episodes}
                            metadataType={ContentType.DetailsEpisode}
                            initialScrollIndex={initialScrollIndex}
                            // @ts-ignore
                            onPress={onSelectEpisode}
                            templateId={templateId}
                            onEndReached={onLoadNextEpisodes}
                        />
                    )}
                </>
            )}
        </>
    );
};
